// @flow
import Backbone from "backbone";

import { request } from "common/components/request";
import {
    fromQueryString
} from "common/utils";

/**
 * Backbone.ajax override
 * Use our request method instead of Backbone + jQuery
 */
Backbone.ajax = (opts) => {
    const options = {
        ...opts,
        method: opts.type || "GET"
    };
    
    delete options.type;
    delete options.success;
    delete options.error;
    
    if (typeof options.data === "string") {
        if (options.method === "GET") {
            options.data = fromQueryString(options.data);
        } else {
            options.data = JSON.parse(options.data);
        }
    }
    
    return request(opts.url, options).then((resp) => {
        if (opts.success) {
            opts.success(resp);
        }
    
        return resp;
    }).catch((resp) => {
        // Check if resp here is an error that we have to throw down the chain
        if (opts.error) {
            opts.error(resp);
        }
    
        throw resp;
    });
};

/**
 * Backbone.History overloads
 * Add catch-all route support. We listen for `routeNotFound` event in main merchantRouter
 */
const loadUrl = Backbone.History.prototype.loadUrl;
const decodeFragment = Backbone.History.prototype.decodeFragment;
Backbone.History.prototype.loadUrl = function(fragment: string): boolean {
    const matched = loadUrl.call(this, fragment);
    
    if (!matched) {
        this.trigger("routeNotFound", fragment);
    }
    
    return matched;
};
Backbone.History.prototype.decodeFragment = function(fragment: string): string {
    return decodeFragment.call(this, fragment).replace(/ /g, "%20");
};

/**
 * Backbone.Model utilities
 */
Backbone.Model.makeProxy = function() {
    const Source = this;
    const ProxyModel = Source.extend({
        proxy: true,
        initialize(...args) {
            Source.prototype.initialize.call(this, args);
            this.on("change", (model) => {
                // eslint-disable-next-line eqeqeq
                if (this.proxyTo != null) {
                    this.proxyTo.set(model.changedAttributes());
                }
            });
        },
        link(anotherSourceInstance) {
            // eslint-disable-next-line eqeqeq
            if (this.proxyTo != null) {
                this.stopListening(this.proxyTo);
                this.proxyTo.stopListening();
                this.proxyTo.off();
            }
            
            this.proxyTo = anotherSourceInstance;
            this.set(anotherSourceInstance.toJSON());
            
            // Proxy events from proxyTo model back the original
            this.listenTo(this.proxyTo, "change", () => this.set(this.proxyTo.changedAttributes()));
            
            this.trigger("linked");
        },
        
        /**
         *  Model that only stores a pointer, should never be saved
         *  This ensures that even if save is called, then it will noop
         */
        save: () => {}
    });
    
    return new ProxyModel();
};

// eslint-disable-next-line import/no-default-export
export default Backbone;
